// pages/admin/WarehouseManagement.tsx 

import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
  Drawer,
  CircularProgress,
  Alert,
  Snackbar,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Avatar,
  Tooltip,
  Breadcrumbs,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Grid,
  InputAdornment,
  Divider,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  Delete,
  Edit,
  Add,
  Refresh,
  NavigateNext,
  Home,
  Close,
  Warehouse as WarehouseIcon,
  LocationOn,
  MyLocation,
  Search,
  Star,
  StarBorder,
} from '@mui/icons-material';

import Slidemenu from '../../components/admin/adminSlidemenu.tsx';
import AdminHeader from '../../components/admin/adminHeader.tsx';

import {
  useListWarehousesQuery,
  useAddWarehouseMutation,
  useUpdateWarehouseMutation,
  useDeleteWarehouseMutation,
  Warehouse,
  WarehouseFormData,
  STATUS_OPTIONS,
  validateCoordinate,
} from '../../features/admin/warehouseApi.ts';

// Shopify-like color palette
const shopifyColors = {
  primary: '#008060',
  primaryLight: '#00a47c',
  primaryDark: '#006e52',
  background: '#f6f6f7',
  surface: '#ffffff',
  border: '#e1e3e5',
  textPrimary: '#202223',
  textSecondary: '#6d7175',
  success: '#008060',
  warning: '#ffc453',
  error: '#d72c0d',
  blue: '#2c6ecb',
  purple: '#5c6ac4',
};

const WarehouseManagement = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [mobileOpen, setMobileOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });

  // Filter states
  const [searchStr, setSearchStr] = useState('');
  const [statusFilter, setStatusFilter] = useState<number | ''>('');

  // Form related states
  const [formOpen, setFormOpen] = useState(false);
  const [editingWarehouse, setEditingWarehouse] = useState<Warehouse | null>(null);
  const [formData, setFormData] = useState<WarehouseFormData>({
    name: '',
    latitude: '',
    longitude: '',
    status: 1,
    is_default: 0,
  });
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});

  // API hooks
  const {
    data,
    isLoading,
    error,
    isError,
    refetch,
  } = useListWarehousesQuery();

  const [addWarehouse, { isLoading: isAdding }] = useAddWarehouseMutation();
  const [updateWarehouse, { isLoading: isUpdating }] = useUpdateWarehouseMutation();
  const [deleteWarehouse, { isLoading: isDeleting }] = useDeleteWarehouseMutation();

  // Handle API response structure
  const warehouses: Warehouse[] = data?.warehouses || [];
  const totalRecords = warehouses.length;

  const isEditing = Boolean(editingWarehouse);
  const isFormLoading = isAdding || isUpdating;

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // helper: unset previous default warehouse (only one default allowed)
  const unsetExistingDefaultWarehouse = async (currentId?: number) => {
    const existingDefault = warehouses.find(
      (w: Warehouse) => w.is_default === 1 && w.id !== currentId
    );
    if (existingDefault) {
      try {
        await updateWarehouse({
          id: existingDefault.id,
          name: existingDefault.name,
          latitude: existingDefault.latitude,
          longitude: existingDefault.longitude,
          status: existingDefault.status,
          is_default: 0,
        }).unwrap();
      } catch (err) {
        console.error('Failed to unset previous default warehouse:', err);
      }
    }
  };

  // Form handling functions
  const resetForm = () => {
    setFormData({
      name: '',
      latitude: '',
      longitude: '',
      status: 1,
      is_default: 0,
    });
    setFormErrors({});
    setEditingWarehouse(null);
  };

  const handleAddWarehouse = () => {
    resetForm();
    setFormOpen(true);
  };

  const handleEditWarehouse = (warehouse: Warehouse) => {
    setFormData({
      name: warehouse.name || '',
      latitude: warehouse.latitude || '',
      longitude: warehouse.longitude || '',
      status: warehouse.status || 1,
      id: warehouse.id,
      is_default: warehouse.is_default ?? 0,
    });
    setEditingWarehouse(warehouse);
    setFormOpen(true);
  };

  const handleFormClose = () => {
    setFormOpen(false);
    resetForm();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleSelectChange = (e: any) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: Number(value) }));

    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleDefaultChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setFormData((prev) => ({ ...prev, is_default: checked ? 1 : 0 }));
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Warehouse name is required';
    }

    if (!formData.latitude.trim()) {
      newErrors.latitude = 'Latitude is required';
    } else if (!validateCoordinate(formData.latitude, 'latitude')) {
      newErrors.latitude = 'Invalid latitude (must be between -90 and 90)';
    }

    if (!formData.longitude.trim()) {
      newErrors.longitude = 'Longitude is required';
    } else if (!validateCoordinate(formData.longitude, 'longitude')) {
      newErrors.longitude = 'Invalid longitude (must be between -180 and 180)';
    }

    setFormErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      // if this warehouse is set as default, unset previous default
      if (formData.is_default === 1) {
        await unsetExistingDefaultWarehouse(editingWarehouse?.id);
      }

      let result;
      if (isEditing && editingWarehouse) {
        const updateData = {
          id: formData.id || editingWarehouse.id,
          name: formData.name.trim(),
          latitude: formData.latitude.trim(),
          longitude: formData.longitude.trim(),
          status: formData.status,
          is_default: formData.is_default ?? 0,
        };

        result = await updateWarehouse(updateData).unwrap();
        setSnackbar({
          open: true,
          message: result?.message || 'Warehouse updated successfully!',
          severity: 'success',
        });
      } else {
        const addData = {
          name: formData.name.trim(),
          latitude: formData.latitude.trim(),
          longitude: formData.longitude.trim(),
          status: formData.status,
          is_default: formData.is_default ?? 0,
        };

        result = await addWarehouse(addData).unwrap();
        setSnackbar({
          open: true,
          message: result?.message || 'Warehouse created successfully!',
          severity: 'success',
        });
      }
      handleFormClose();
      refetch();
    } catch (err: any) {
      console.error('Failed to save warehouse:', err);

      let errorMessage = `Error ${isEditing ? 'updating' : 'creating'} warehouse. Please try again.`;

      if (err?.data?.message) {
        errorMessage = err.data.message;
      } else if (err?.data?.error) {
        errorMessage = typeof err.data.error === 'string' ? err.data.error : 'Validation failed';
      } else if (err?.message) {
        errorMessage = err.message;
      }

      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error',
      });
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this warehouse?')) {
      try {
        const result = await deleteWarehouse({ id }).unwrap();
        setSnackbar({
          open: true,
          message: result?.message || 'Warehouse deleted successfully!',
          severity: 'success',
        });
        refetch();
      } catch (err: any) {
        console.error('Failed to delete warehouse:', err);

        let errorMessage = 'Error deleting warehouse. Please try again.';

        if (err?.data?.message) {
          errorMessage = err.data.message;
        } else if (err?.data?.error) {
          errorMessage = typeof err.data.error === 'string' ? err.data.error : 'Delete operation failed';
        } else if (err?.message) {
          errorMessage = err.message;
        }

        setSnackbar({
          open: true,
          message: errorMessage,
          severity: 'error',
        });
      }
    }
  };

  const handleSetDefault = async (warehouse: Warehouse) => {
    try {
      await unsetExistingDefaultWarehouse(warehouse.id);

      const result = await updateWarehouse({
        id: warehouse.id,
        name: warehouse.name,
        latitude: warehouse.latitude,
        longitude: warehouse.longitude,
        status: warehouse.status,
        is_default: 1,
      }).unwrap();

      setSnackbar({
        open: true,
        message: result?.message || 'Default warehouse updated',
        severity: 'success',
      });
      refetch();
    } catch (err: any) {
      console.error('Failed to set default warehouse:', err);

      let errorMessage = 'Error setting default warehouse. Please try again.';

      if (err?.data?.message) {
        errorMessage = err.data.message;
      } else if (err?.data?.error) {
        errorMessage = typeof err.data.error === 'string' ? err.data.error : 'Operation failed';
      } else if (err?.message) {
        errorMessage = err.message;
      }

      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error',
      });
    }
  };

  const handleRefresh = () => {
    refetch();
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const renderErrorMessage = () => {
    if (error && 'status' in error) {
      switch (error.status) {
        case 403:
          return "You don't have permission to view warehouses.";
        case 401:
          return 'Please sign in to continue.';
        case 404:
          return 'No warehouses found.';
        case 500:
          return 'Something went wrong. Please try again.';
        default:
          return 'Unable to load warehouses. Please try again.';
      }
    }
    return 'Network error. Please check your connection.';
  };

  const getStatusChip = (status: number) => {
    const statusOption = STATUS_OPTIONS.find((opt) => opt.value === status);
    return (
      <Chip
        label={statusOption?.label || 'Unknown'}
        size="small"
        sx={{
          borderRadius: '4px',
          fontWeight: 500,
          backgroundColor: status === 1 ? shopifyColors.success : shopifyColors.textSecondary,
          color: 'white',
          fontSize: '0.75rem',
        }}
      />
    );
  };

  // Filter warehouses based on search and status
  const filteredWarehouses = warehouses.filter((warehouse: Warehouse) => {
    const matchesSearch =
      !searchStr || warehouse.name?.toLowerCase().includes(searchStr.toLowerCase());

    const matchesStatus = statusFilter === '' || warehouse.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', backgroundColor: shopifyColors.background }}>
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Sidebar */}
        <Box sx={{ width: '250px', flexShrink: 0, display: { xs: 'none', md: 'block' } }}>
          <Slidemenu mobileOpen={false} onToggle={handleDrawerToggle} />
        </Box>

        {/* Drawer for mobile */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { width: 250 },
          }}
        >
          <Slidemenu onToggle={handleDrawerToggle} mobileOpen={false} />
        </Drawer>

        {/* Main Content */}
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <AdminHeader onToggleMenu={handleDrawerToggle} />

          {/* Page Header */}
          <Box sx={{ p: 3, pb: 2, backgroundColor: shopifyColors.surface }}>
            <Breadcrumbs separator={<NavigateNext fontSize="small" />} sx={{ mb: 2 }}>
              <Link color="inherit" href="#" underline="hover">
                <Home sx={{ fontSize: 20 }} />
              </Link>
              <Link color="inherit" href="DeliveryAgentManagement" underline="hover">
                Delivery
              </Link>
              <Typography color={shopifyColors.textPrimary} sx={{ fontWeight: 600 }}>
                Warehouses
              </Typography>
            </Breadcrumbs>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    color: shopifyColors.textPrimary,
                    mb: 1,
                  }}
                >
                  Warehouses
                </Typography>
                <Typography variant="body1" sx={{ color: shopifyColors.textSecondary }}>
                  Manage your warehouse locations and inventory centers
                </Typography>
              </Box>

              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleAddWarehouse}
                size="large"
                sx={{
                  backgroundColor: shopifyColors.primary,
                  borderRadius: '6px',
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3,
                  '&:hover': {
                    backgroundColor: shopifyColors.primaryLight,
                  },
                }}
              >
                Add warehouse
              </Button>
            </Box>
          </Box>

          {/* Main Content Area */}
          <Box sx={{ flexGrow: 1, p: 3, pt: 2, overflow: 'auto' }}>
            {/* Filter Card */}
            <Card sx={{ mb: 3, borderRadius: '8px', border: `1px solid ${shopifyColors.border}` }}>
              <CardContent sx={{ p: '16px !important' }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                  {/* Status Filter */}
                  <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value as number | '')}
                      label="Status"
                      sx={{ borderRadius: '6px' }}
                    >
                      <MenuItem value="">
                        <em>All Statuses</em>
                      </MenuItem>
                      {STATUS_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {/* Search */}
                  <TextField
                    size="small"
                    placeholder="Search warehouses..."
                    value={searchStr}
                    onChange={(e) => setSearchStr(e.target.value)}
                    sx={{
                      minWidth: 250,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '6px',
                      },
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search fontSize="small" sx={{ color: shopifyColors.textSecondary }} />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <Box sx={{ flexGrow: 1 }} />

                  <Button
                    variant="outlined"
                    startIcon={<Refresh />}
                    onClick={handleRefresh}
                    disabled={isLoading}
                    size="small"
                    sx={{
                      borderRadius: '6px',
                      textTransform: 'none',
                      borderColor: shopifyColors.border,
                      color: shopifyColors.textPrimary,
                    }}
                  >
                    Refresh
                  </Button>
                </Box>
              </CardContent>
            </Card>

            {/* Results Summary */}
            {!isLoading && !isError && (
              <Typography variant="body2" sx={{ color: shopifyColors.textSecondary, mb: 2 }}>
                {filteredWarehouses.length} warehouse{filteredWarehouses.length === 1 ? '' : 's'}
                {searchStr && ` matching "${searchStr}"`}
              </Typography>
            )}

            {/* Loading State */}
            {isLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                <CircularProgress sx={{ color: shopifyColors.primary }} />
                <Typography sx={{ ml: 2, color: shopifyColors.textSecondary }}>Loading warehouses...</Typography>
              </Box>
            )}

            {/* Error State */}
            {isError && !isLoading && (
              <Alert
                severity="error"
                sx={{
                  mb: 2,
                  borderRadius: '6px',
                  border: `1px solid ${shopifyColors.error}20`,
                }}
                action={
                  <Button color="inherit" size="small" onClick={handleRefresh}>
                    Try again
                  </Button>
                }
              >
                {renderErrorMessage()}
              </Alert>
            )}

            {/* Empty State */}
            {!isLoading && !isError && filteredWarehouses.length === 0 && (
              <Card sx={{ textAlign: 'center', py: 8, borderRadius: '8px' }}>
                <WarehouseIcon sx={{ fontSize: 64, color: shopifyColors.textSecondary, mb: 2, opacity: 0.5 }} />
                <Typography variant="h6" sx={{ color: shopifyColors.textPrimary, mb: 1, fontWeight: 600 }}>
                  {searchStr ? 'No warehouses found' : 'No warehouses yet'}
                </Typography>
                <Typography variant="body2" sx={{ color: shopifyColors.textSecondary, mb: 3 }}>
                  {searchStr
                    ? `No warehouses match your search "${searchStr}"`
                    : 'Add your first warehouse to get started'}
                </Typography>
                {!searchStr && (
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={handleAddWarehouse}
                    sx={{
                      backgroundColor: shopifyColors.primary,
                      borderRadius: '6px',
                      textTransform: 'none',
                      fontWeight: 600,
                      '&:hover': {
                        backgroundColor: shopifyColors.primaryLight,
                      },
                    }}
                  >
                    Add warehouse
                  </Button>
                )}
              </Card>
            )}

            {/* Warehouses List */}
            {!isLoading && filteredWarehouses.length > 0 && (
              <Card sx={{ borderRadius: '8px', border: `1px solid ${shopifyColors.border}`, overflow: 'hidden' }}>
                <List sx={{ p: 0 }}>
                  {filteredWarehouses.map((warehouse: Warehouse, index: number) => (
                    <ListItem
                      key={warehouse.id}
                      sx={{
                        borderBottom:
                          index < filteredWarehouses.length - 1 ? `1px solid ${shopifyColors.border}` : 'none',
                        '&:hover': {
                          backgroundColor: `${shopifyColors.background}80`,
                        },
                        py: 2.5,
                      }}
                    >
                      <ListItemIcon>
                        <Avatar
                          sx={{
                            bgcolor: shopifyColors.primary,
                            width: 48,
                            height: 48,
                          }}
                        >
                          <WarehouseIcon />
                        </Avatar>
                      </ListItemIcon>

                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: shopifyColors.textPrimary }}>
                              {warehouse.name || 'Unnamed Warehouse'}
                            </Typography>
                            {getStatusChip(warehouse.status)}
                            {warehouse.is_default === 1 && (
                              <Chip
                                label="Default"
                                size="small"
                                icon={<Star sx={{ fontSize: 14 }} />}
                                sx={{
                                  borderRadius: '4px',
                                  fontWeight: 600,
                                  backgroundColor: shopifyColors.blue,
                                  color: 'white',
                                  fontSize: '0.7rem',
                                }}
                              />
                            )}
                          </Box>
                        }
                        secondary={
                          <Box sx={{ mt: 1 }}>
                            <Typography variant="body2" sx={{ color: shopifyColors.textSecondary, mb: 0.5 }}>
                              ID: {warehouse.id}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <LocationOn sx={{ fontSize: 16, color: shopifyColors.textSecondary }} />
                              <Typography variant="body2" sx={{ color: shopifyColors.textSecondary }}>
                                Lat: {warehouse.latitude}, Long: {warehouse.longitude}
                              </Typography>
                            </Box>
                          </Box>
                        }
                      />

                      <ListItemSecondaryAction>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          {warehouse.is_default !== 1 && (
                            <Tooltip title="Set as default">
                              <IconButton onClick={() => handleSetDefault(warehouse)} size="small">
                                <StarBorder />
                              </IconButton>
                            </Tooltip>
                          )}
                          {warehouse.is_default === 1 && (
                            <Tooltip title="Default warehouse">
                              <span>
                                <IconButton size="small" disabled>
                                  <Star />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}

                          <Tooltip title="Edit">
                            <IconButton onClick={() => handleEditWarehouse(warehouse)} size="small">
                              <Edit />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Delete">
                            <IconButton onClick={() => handleDelete(warehouse.id)} disabled={isDeleting} size="small">
                              <Delete />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              </Card>
            )}
          </Box>
        </Box>
      </Box>

      {/* Add/Edit Dialog */}
      <Dialog
        open={formOpen}
        onClose={handleFormClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '8px',
            border: `1px solid ${shopifyColors.border}`,
          },
        }}
      >
        <DialogTitle
          sx={{
            pb: 2,
            borderBottom: `1px solid ${shopifyColors.border}`,
            backgroundColor: shopifyColors.background,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: shopifyColors.textPrimary }}>
              {isEditing ? 'Edit warehouse' : 'Add warehouse'}
            </Typography>
            <IconButton
              onClick={handleFormClose}
              size="small"
              disabled={isFormLoading}
              sx={{ color: shopifyColors.textSecondary }}
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ pt: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Warehouse Name */}
              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: shopifyColors.textPrimary,
                    fontWeight: 600,
                    mb: 2,
                  }}
                >
                  Basic Information
                </Typography>

                <TextField
                  name="name"
                  label="Warehouse Name"
                  value={formData.name}
                  onChange={handleInputChange}
                  error={!!formErrors.name}
                  helperText={formErrors.name || 'Enter a descriptive name for the warehouse'}
                  fullWidth
                  required
                  placeholder="e.g., Dubai Main Warehouse"
                  disabled={isFormLoading}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '6px',
                    },
                  }}
                />
              </Box>

              <Divider />

              {/* Location Information */}
              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: shopifyColors.textPrimary,
                    fontWeight: 600,
                    mb: 2,
                  }}
                >
                  Location Coordinates
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="latitude"
                      label="Latitude"
                      value={formData.latitude}
                      onChange={handleInputChange}
                      error={!!formErrors.latitude}
                      helperText={formErrors.latitude || 'Range: -90 to 90'}
                      fullWidth
                      required
                      placeholder="25.2048"
                      disabled={isFormLoading}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <MyLocation sx={{ fontSize: 20, color: shopifyColors.textSecondary }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '6px',
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="longitude"
                      label="Longitude"
                      value={formData.longitude}
                      onChange={handleInputChange}
                      error={!!formErrors.longitude}
                      helperText={formErrors.longitude || 'Range: -180 to 180'}
                      fullWidth
                      required
                      placeholder="55.2708"
                      disabled={isFormLoading}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <MyLocation sx={{ fontSize: 20, color: shopifyColors.textSecondary }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '6px',
                        },
                      }}
                    />
                  </Grid>
                </Grid>

                <Typography
                  variant="caption"
                  sx={{ color: shopifyColors.textSecondary, mt: 1, display: 'block' }}
                >
                  💡 Tip: Use Google Maps to find exact coordinates for your warehouse location
                </Typography>
              </Box>

              <Divider />

              {/* Status + Default */}
              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: shopifyColors.textPrimary,
                    fontWeight: 600,
                    mb: 2,
                  }}
                >
                  Warehouse Status
                </Typography>

                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    name="status"
                    value={formData.status}
                    onChange={handleSelectChange}
                    label="Status"
                    sx={{ borderRadius: '6px' }}
                    disabled={isFormLoading}
                  >
                    {STATUS_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.is_default === 1}
                      onChange={handleDefaultChange}
                      disabled={isFormLoading}
                    />
                  }
                  label="Set as default warehouse"
                />
                <Typography variant="caption" sx={{ color: shopifyColors.textSecondary, display: 'block' }}>
                  Only one warehouse will be treated as default. Setting this as default will remove the
                  default flag from any previously selected warehouse.
                </Typography>
              </Box>
            </Box>
          </DialogContent>

          <DialogActions
            sx={{
              p: 3,
              pt: 2,
              gap: 2,
              borderTop: `1px solid ${shopifyColors.border}`,
            }}
          >
            <Button
              onClick={handleFormClose}
              disabled={isFormLoading}
              variant="outlined"
              sx={{
                borderRadius: '6px',
                textTransform: 'none',
                fontWeight: 600,
                borderColor: shopifyColors.border,
                color: shopifyColors.textPrimary,
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={
                isFormLoading ||
                !formData.name?.trim() ||
                !formData.latitude?.trim() ||
                !formData.longitude?.trim()
              }
              sx={{
                backgroundColor: shopifyColors.primary,
                borderRadius: '6px',
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                '&:hover': {
                  backgroundColor: shopifyColors.primaryLight,
                },
              }}
            >
              {isFormLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} color="inherit" />
                  {isEditing ? 'Saving...' : 'Adding...'}
                </Box>
              ) : isEditing ? (
                'Save changes'
              ) : (
                'Add warehouse'
              )}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{
            width: '100%',
            borderRadius: '6px',
            alignItems: 'center',
          }}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default WarehouseManagement;
